import { useQueryClient } from '@tanstack/react-query';
import { createContext, memo, type ReactNode, useCallback, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { getPrivateSkillKey, type UsePrivateSkillParams } from '~/hooks/queries/skills/usePrivateSkill';
import {
  type Skill,
  type UsePrivateSkillsListParams,
  usePrivateSkillsList,
} from '~/hooks/queries/skills/usePrivateSkillsList';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PrivateWorkspaceAgentSkillsCatalogContextValue = {
  fetchSkillsListNextPage: () => void;
  /**
   * Resolves from loaded list pages first, then the react-query cache for `usePrivateSkill` when the
   * skill id is not on a fetched page yet (e.g. history monikers before the user scrolls the catalog).
   */
  getSkillById: (skillId: string) => Skill | undefined;
  hasSkillsListNextPage: boolean;
  isSkillsCatalogEnabled: boolean;
  isSkillsListFetchNextPageError: boolean;
  /** True while the first `Skills.list` page for this workspace is in flight. */
  isSkillsListLoading: boolean;
  isSkillsListFetchingNextPage: boolean;
  /** `enableSkillsUi && workspaceAgentSkillsEnabled && workspaceId` — slash menu, placeholder, auto-format */
  isWorkspaceAgentSlashSkillsEnabled: boolean;
  skills: readonly Skill[];
  skillsById: ReadonlyMap<string, Skill>;
  workspaceId: string | undefined;
};

const defaultPrivateWorkspaceAgentSkillsCatalogContextValue: PrivateWorkspaceAgentSkillsCatalogContextValue = {
  fetchSkillsListNextPage: () => undefined,
  getSkillById: () => undefined,
  hasSkillsListNextPage: false,
  isSkillsCatalogEnabled: false,
  isSkillsListFetchNextPageError: false,
  isSkillsListLoading: false,
  isSkillsListFetchingNextPage: false,
  isWorkspaceAgentSlashSkillsEnabled: false,
  skills: [],
  skillsById: new Map(),
  workspaceId: undefined,
};

const PrivateWorkspaceAgentSkillsCatalogContext = createContext<PrivateWorkspaceAgentSkillsCatalogContextValue>(
  defaultPrivateWorkspaceAgentSkillsCatalogContextValue,
);

export type PrivateWorkspaceAgentSkillsCatalogProviderProps = {
  children: ReactNode;
  workspaceId: string | undefined;
};

export const PrivateWorkspaceAgentSkillsCatalogProvider = memo(
  ({ children, workspaceId }: PrivateWorkspaceAgentSkillsCatalogProviderProps) => {
    const { enableSkillsUi, workspaceAgentSkillsEnabled } = useTemporaryFeatureFlags();
    const isSkillsCatalogEnabled = enableSkillsUi && !!workspaceId;
    const isWorkspaceAgentSlashSkillsEnabled = isSkillsCatalogEnabled && workspaceAgentSkillsEnabled;

    const listParams = useMemo(
      (): UsePrivateSkillsListParams => ({ omitSkillListType: true, workspaceId }),
      [workspaceId],
    );

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError, isLoading } =
      usePrivateSkillsList(listParams, {
        enabled: isSkillsCatalogEnabled,
      });

    const fetchSkillsListNextPage = useCallback(() => {
      void fetchNextPage();
    }, [fetchNextPage]);

    const isSkillsListLoading = isLoading;

    const skills = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

    const skillsById = useMemo(() => new Map(skills.map((skill) => [skill.id, skill])), [skills]);

    const queryClient = useQueryClient();

    const getSkillById = useCallback(
      (skillId: string): Skill | undefined => {
        const fromList = skillsById.get(skillId);

        if (fromList) {
          return fromList;
        }

        if (!workspaceId) {
          return undefined;
        }

        const getParams: UsePrivateSkillParams = { skillId, workspaceId };
        const cached = queryClient.getQueryData<Skill>(getPrivateSkillKey(getParams));

        return cached ?? undefined;
      },
      [queryClient, skillsById, workspaceId],
    );

    const value = useMemo(
      (): PrivateWorkspaceAgentSkillsCatalogContextValue => ({
        fetchSkillsListNextPage,
        getSkillById,
        hasSkillsListNextPage: Boolean(hasNextPage),
        isSkillsCatalogEnabled,
        isSkillsListFetchNextPageError: isFetchNextPageError,
        isSkillsListLoading,
        isSkillsListFetchingNextPage: isFetchingNextPage,
        isWorkspaceAgentSlashSkillsEnabled,
        skills,
        skillsById,
        workspaceId,
      }),
      [
        fetchSkillsListNextPage,
        getSkillById,
        hasNextPage,
        isFetchNextPageError,
        isFetchingNextPage,
        isSkillsListLoading,
        isSkillsCatalogEnabled,
        isWorkspaceAgentSlashSkillsEnabled,
        skills,
        skillsById,
        workspaceId,
      ],
    );

    return (
      <PrivateWorkspaceAgentSkillsCatalogContext.Provider value={value}>
        {children}
      </PrivateWorkspaceAgentSkillsCatalogContext.Provider>
    );
  },
);

PrivateWorkspaceAgentSkillsCatalogProvider.displayName = 'PrivateWorkspaceAgentSkillsCatalogProvider';

export const usePrivateWorkspaceAgentSkillsCatalog = (): PrivateWorkspaceAgentSkillsCatalogContextValue => {
  const context = useContext(PrivateWorkspaceAgentSkillsCatalogContext);

  if (context === defaultPrivateWorkspaceAgentSkillsCatalogContextValue) {
    const error = new Error(
      'usePrivateWorkspaceAgentSkillsCatalog must be used within PrivateWorkspaceAgentSkillsCatalogProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      context: 'Private workspace agent skills catalog context not found',
      error,
    });
  }

  return context;
};
